import { Directive, forwardRef, inject, Injector, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subscription } from 'rxjs';

import { validateCpfCnpj } from '../validators/cpf-cnpj.validator';

/**
 * Validator directive for Brazilian CPF/CNPJ documents.
 * Auto-detects CPF (11 digits) vs CNPJ (14 digits).
 *
 * Validation timing:
 * - Initial validation: on blur only
 * - Re-validation after error: on valueChanges (clears instantly when valid)
 *
 * Usage:
 * ```html
 * <input type="text" [(ngModel)]="document" appCpfCnpj #docControl="ngModel">
 * @if (docControl.errors?.['cpfCnpj']) {
 *   <span>{{ getErrorMessage(docControl.errors['cpfCnpj']) }}</span>
 * }
 * ```
 */
@Directive({
  selector: '[appCpfCnpj]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CpfCnpjValidatorDirective),
      multi: true,
    },
  ],
  host: {
    '(blur)': 'onBlur()',
  },
})
export class CpfCnpjValidatorDirective implements Validator, OnDestroy {
  private _injector = inject(Injector);
  private _ngControl: NgControl | null = null;
  private _valueChangesSub: Subscription | null = null;

  private _hasBlurred = false;
  private _lastError: ValidationErrors | null = null;

  /**
   * Lazily resolve NgControl to avoid circular dependency.
   * NgControl depends on validators, and this directive is a validator.
   */
  private get ngControl(): NgControl | null {
    if (!this._ngControl) {
      try {
        this._ngControl = this._injector.get(NgControl, null);
      } catch {
        this._ngControl = null;
      }
    }
    return this._ngControl;
  }

  ngOnDestroy(): void {
    this._valueChangesSub?.unsubscribe();
  }

  /**
   * Validator function called by Angular forms.
   */
  validate(control: AbstractControl): ValidationErrors | null {
    // Don't validate until user has blurred at least once
    if (!this._hasBlurred) {
      return null;
    }

    this._lastError = validateCpfCnpj(control.value);
    return this._lastError;
  }

  /**
   * Handle blur event - trigger validation and subscribe to valueChanges.
   */
  onBlur(): void {
    this._hasBlurred = true;

    // Trigger validation update
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();

      // Subscribe to valueChanges to re-validate when value changes (hybrid approach)
      // This ensures we see the final synchronized value after all transformations
      // (especially important when used with ControlValueAccessor-based mask directives)
      if (!this._valueChangesSub) {
        this._valueChangesSub = this.ngControl.control.valueChanges.subscribe(() => {
          // Only re-validate if there was an error (to clear it when valid)
          if (this._lastError && this.ngControl?.control) {
            // Use emitEvent: false to prevent infinite loop - updateValueAndValidity
            // would otherwise emit valueChanges, triggering this subscription again
            this.ngControl.control.updateValueAndValidity({ emitEvent: false });
          }
        });
      }
    }
  }
}
